<script setup>
// Super-admin AI Configuration (platform surface, /ds-ops/ai-config).
//
// Two things on one page:
//  1. Role → service assignment — which provider/model handles each of i-Recall's
//     five AI roles (STT, TTS, distractors, review, hints). Saved to
//     /api/platform/ai-config. STT/TTS are OpenAI-only (locked). NOTE: the
//     runtime does not consume these yet — that wiring is a later phase; this is
//     the control surface. A banner says so.
//  2. Provider connection checks + health — status, a live Test Connection, and
//     an on-demand health check, via saas-core's already-mounted
//     /api/platform/ai-providers. This is what surfaces e.g. an OpenAI quota
//     failure right now. No cost tracking (by design).
import { ref, computed, onMounted } from "vue";

const CONFIG_URL = "/api/platform/ai-config";
const PROVIDERS_URL = "/api/platform/ai-providers";
const PROVIDER_NAMES = { openai: "OpenAI", claude: "Anthropic Claude", gemini: "Google Gemini" };

const loading = ref(true);
const loadError = ref("");

// role config
const roles = ref([]);            // catalogue [{ key, label, detail, providers, locked, ... }]
const assignments = ref({});      // { roleKey: { provider, model } }
const saving = ref(false);
const saveError = ref("");
const saveProblems = ref([]);
const saved = ref(false);

// provider status / health (from saas-core)
const providerStatus = ref({});   // { openai: { name, set, model, costPerMTokens }, ... }
const health = ref(null);
const testing = ref({});          // { provider: bool }
const testResult = ref({});       // { provider: { ok, responseTime, error, model } }
const runningHealth = ref(false);

const providerOrder = ["openai", "claude", "gemini"];
const providers = computed(() =>
  providerOrder
    .filter((k) => providerStatus.value[k] || k)
    .map((k) => ({ key: k, name: providerStatus.value[k]?.name || PROVIDER_NAMES[k] || k, ...providerStatus.value[k] }))
);
const nameFor = (p) => PROVIDER_NAMES[p] || p;

async function loadAll() {
  loading.value = true; loadError.value = "";
  try {
    const [cfgR, provR] = await Promise.all([
      fetch(CONFIG_URL, { credentials: "include" }),
      fetch(PROVIDERS_URL, { credentials: "include" })
    ]);
    if (!cfgR.ok) throw new Error("Could not load AI config.");
    const cfg = await cfgR.json();
    roles.value = cfg.roles || [];
    assignments.value = cfg.assignments || {};
    if (provR.ok) {
      const p = await provR.json();
      providerStatus.value = p.providers || {};
      health.value = p.health || null;
    }
  } catch (e) {
    loadError.value = e.message || "Could not load this page.";
  } finally {
    loading.value = false;
  }
}

async function save() {
  saving.value = true; saveError.value = ""; saveProblems.value = []; saved.value = false;
  try {
    const r = await fetch(CONFIG_URL, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ assignments: assignments.value })
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) { if (Array.isArray(data.problems)) saveProblems.value = data.problems; throw new Error(data.error || "Could not save."); }
    assignments.value = data.assignments || assignments.value;
    saved.value = true;
    setTimeout(() => { saved.value = false; }, 2500);
  } catch (e) {
    saveError.value = e.message;
  } finally {
    saving.value = false;
  }
}

async function testConnection(provider) {
  testing.value = { ...testing.value, [provider]: true };
  testResult.value = { ...testResult.value, [provider]: null };
  try {
    const r = await fetch(`${PROVIDERS_URL}/test-connection`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ provider })
    });
    const data = await r.json().catch(() => ({}));
    testResult.value = { ...testResult.value, [provider]: { ok: r.ok && data.ok !== false, ...data } };
  } catch (e) {
    testResult.value = { ...testResult.value, [provider]: { ok: false, error: e.message } };
  } finally {
    testing.value = { ...testing.value, [provider]: false };
  }
}

async function runHealthCheck() {
  runningHealth.value = true;
  try {
    const r = await fetch(`${PROVIDERS_URL}/health-check`, { method: "POST", credentials: "include" });
    const data = await r.json().catch(() => ({}));
    if (r.ok) health.value = data.health || data || health.value;
  } catch { /* keep prior health */ } finally {
    runningHealth.value = false;
  }
}

const healthClass = (s) => ({ healthy: "text-success", degraded: "text-warning", critical: "text-danger" }[s] || "text-muted");

onMounted(loadAll);
</script>

<template>
  <div class="container-fluid p-0">
    <h1 class="h3 mb-1">AI Configuration</h1>
    <p class="text-muted">Which AI service handles each job, and whether each provider is reachable.</p>

    <div v-if="loading" class="text-muted py-4"><span class="spinner-border spinner-border-sm me-2"></span>Loading…</div>
    <div v-else-if="loadError" class="alert alert-warning">{{ loadError }}</div>

    <template v-else>
      <!-- Phase note -->
      <div class="alert alert-info d-flex align-items-start py-2">
        <i class="bi bi-info-circle me-2 mt-1"></i>
        <div class="small">
          Assignments below are <strong>saved</strong> but the runtime still uses the built-in defaults for now —
          it begins honouring these in a later phase. <strong>Test Connection</strong> and the health check are live.
          Keys are managed as environment variables (editing keys in-app arrives with the runtime wiring).
        </div>
      </div>

      <!-- Role → service matrix -->
      <div class="card border-0 shadow-sm mb-3">
        <div class="card-body">
          <h2 class="h6 mb-3">Role → service</h2>
          <div class="table-responsive">
            <table class="table table-sm align-middle mb-0">
              <thead>
                <tr class="text-muted small"><th style="min-width:12rem">Role</th><th style="width:11rem">Provider</th><th>Model</th></tr>
              </thead>
              <tbody>
                <tr v-for="role in roles" :key="role.key">
                  <td>
                    <div class="fw-semibold">{{ role.label }}</div>
                    <div class="small text-muted">{{ role.detail }}</div>
                  </td>
                  <td>
                    <select v-if="assignments[role.key]" v-model="assignments[role.key].provider" class="form-select form-select-sm" :disabled="role.locked">
                      <option v-for="p in role.providers" :key="p" :value="p">{{ nameFor(p) }}</option>
                    </select>
                    <div v-if="role.locked" class="small text-muted mt-1"><i class="bi bi-lock me-1"></i>only provider available today</div>
                  </td>
                  <td>
                    <input v-if="assignments[role.key]" v-model="assignments[role.key].model" type="text" class="form-control form-control-sm" placeholder="model id">
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="d-flex align-items-center gap-2 mt-3">
            <button class="btn btn-primary btn-sm" :disabled="saving" @click="save">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>Save assignments
            </button>
            <span v-if="saved" class="text-success small"><i class="bi bi-check-circle me-1"></i>Saved</span>
            <span v-if="saveError" class="text-danger small">{{ saveError }}</span>
          </div>
          <ul v-if="saveProblems.length" class="text-danger small mb-0 mt-1"><li v-for="(p, i) in saveProblems" :key="i">{{ p }}</li></ul>
        </div>
      </div>

      <!-- Providers: status + connection check -->
      <div class="card border-0 shadow-sm mb-3">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h2 class="h6 mb-0">Providers &amp; connection checks</h2>
            <button class="btn btn-outline-secondary btn-sm" :disabled="runningHealth" @click="runHealthCheck">
              <span v-if="runningHealth" class="spinner-border spinner-border-sm me-2"></span>Run health check
            </button>
          </div>
          <div v-if="health" class="small mb-3">
            Overall status: <strong :class="healthClass(health.status)">{{ health.status || "unknown" }}</strong>
          </div>

          <div class="row g-3">
            <div v-for="p in providers" :key="p.key" class="col-12 col-md-4">
              <div class="border rounded p-3 h-100">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="fw-semibold">{{ p.name }}</div>
                  <span v-if="p.set" class="badge bg-success-subtle text-success border border-success-subtle">key set</span>
                  <span v-else class="badge bg-secondary-subtle text-secondary border">no key</span>
                </div>
                <div v-if="p.model" class="small text-muted mt-1">default model: {{ p.model }}</div>

                <button class="btn btn-outline-primary btn-sm w-100 mt-2" :disabled="!p.set || testing[p.key]" @click="testConnection(p.key)">
                  <span v-if="testing[p.key]" class="spinner-border spinner-border-sm me-2"></span>Test Connection
                </button>

                <div v-if="testResult[p.key]" class="small mt-2">
                  <span v-if="testResult[p.key].ok" class="text-success"><i class="bi bi-check-circle me-1"></i>Reachable<span v-if="testResult[p.key].responseTime"> · {{ testResult[p.key].responseTime }}ms</span></span>
                  <span v-else class="text-danger"><i class="bi bi-exclamation-triangle me-1"></i>{{ testResult[p.key].error || "Failed" }}</span>
                </div>
              </div>
            </div>
          </div>
          <p class="text-muted small mb-0 mt-3">Keys are configured as environment variables. Test Connection makes a real (tiny) call to the provider — useful for spotting an invalid key or an exhausted quota.</p>
        </div>
      </div>
    </template>
  </div>
</template>
